import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import LegacyFilterToolbar, {
  LEGACY_FILTER_INPUT_CLASS,
} from "components/Common/LegacyFilterToolbar";
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    FormFeedback,
    Input,
    Row,
} from "reactstrap";
import Select from "react-select";
import * as Yup from "yup";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { normalizeMetadataFilterDateForApi } from "helpers/metadataFilterHelpers";

import LeadsAddedReportTable from "components/Common/LeadsAddedReportTable";
import Loaders from "components/Common/Loaders";
import {
    AsyncClientMultiSelect,
    AsyncUserSelect,
    StaticFilterSelect,
} from "components/Common/AsyncSearchSelects";
import {
    applyUserAsyncFilterOptionToFormState,
    buildUserAsyncFilterInitialOption,
    getUserAsyncFilterLabelFieldKey,
} from "helpers/metadataFilterHelpers";
import LeadsAddedStatusCandidatesModal from "pages/AllCommonModal/LeadsAddedStatusCandidatesModal/leads-added-status-candidates-modal";
import {
    fetchLeadsAddedJobCategoryOptions,
    fetchLeadsAddedTeamOptions,
    submitLeadsAddedReportFilterData,
} from "model/LeadsAddedReportModel";

const BASE_TABLE_COLUMNS = [
    { Header: "SN", accessor: "sn" },
    { Header: "Client", accessor: "client" },
];

const TRAILING_TABLE_COLUMNS = [
    { Header: "Email sent count", accessor: "emailSentCount" },
    { Header: "Last Day when email was sent", accessor: "lastDayWhenEmailWasSent" },
];

const normalizeStatusKey = (value) =>
    String(value ?? "")
        .trim()
        .toLowerCase();

const buildStatusAccessor = (index) => `status_${index}`;

const extractStatusColumnDefinitions = (rows = []) => {
    const list = Array.isArray(rows) ? rows : [];
    const firstRowWithStatusColumns = list.find(
        (row) => Array.isArray(row?.statusColumns) && row.statusColumns.length
    );
    if (!firstRowWithStatusColumns) return [];

    return firstRowWithStatusColumns.statusColumns
        .map((statusColumn = {}) => {
            const statusKey = String(statusColumn?.statusKey ?? "").trim();
            return {
                statusKey,
                statusLabel: String(statusColumn?.statusLabel ?? statusKey).trim(),
            };
        })
        .filter((definition) => definition.statusKey);
};

const buildTableColumns = (statusColumnDefinitions = []) => {
    const statusColumns = (Array.isArray(statusColumnDefinitions)
        ? statusColumnDefinitions
        : []
    ).map((definition, index) => ({
        Header: definition.statusLabel,
        accessor: buildStatusAccessor(index),
        statusKey: definition.statusKey,
        isStatusCount: true,
    }));

    return [...BASE_TABLE_COLUMNS, ...statusColumns, ...TRAILING_TABLE_COLUMNS];
};

const strOrNull = (value) => {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return normalized === "" ? null : normalized;
};

const normalizeId = (value) => String(value ?? "").trim();

const multiselectIdsOrNull = (values = []) => {
    const ids = (Array.isArray(values) ? values : [])
        .map(normalizeId)
        .filter(Boolean);
    return ids.length ? ids : null;
};

const selectZIndexStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
};

const leadsAddedFilterMultiSelectStyles = {
    ...selectZIndexStyles,
    control: (base) => ({
        ...base,
        minHeight: 38,
        maxHeight: 38,
        overflow: "hidden",
    }),
    valueContainer: (base) => ({
        ...base,
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        alignItems: "center",
        overflowX: "auto",
        overflowY: "hidden",
        padding: "2px 6px",
        gap: 4,
        scrollbarWidth: "none",
        msOverflowStyle: "none",
    }),
    multiValue: (base) => ({
        ...base,
        flex: "0 0 auto",
        margin: 0,
        backgroundColor: "#0d1b6e",
        borderRadius: 2,
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: "#fff",
        fontSize: "0.8125rem",
        fontWeight: 500,
        whiteSpace: "nowrap",
        padding: "1px 4px",
    }),
    multiValueRemove: (base) => ({
        ...base,
        color: "#fff",
        backgroundColor: "transparent",
        ":hover": {
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            color: "#fff",
        },
    }),
};

const getSelectedMultiselectOptions = (options = [], selectedValues = []) => {
    const values = Array.isArray(selectedValues) ? selectedValues : [];
    return (Array.isArray(options) ? options : []).filter((option) =>
        values.some((value) => String(value) === String(option?.value))
    );
};

const leadsAddedReportFilterSchema = Yup.object().shape({
    fromDate: Yup.string().trim().required("Start date is required"),
    toDate: Yup.string().trim().required("End date is required"),
});

const FilterFieldFeedback = ({ message = "" }) => (
    <div className="filter-field-feedback-slot" aria-live="polite">
        {message ? (
            <FormFeedback type="invalid" className="d-block filter-field-feedback mb-0">
                {message}
            </FormFeedback>
        ) : null}
    </div>
);

FilterFieldFeedback.propTypes = {
    message: PropTypes.string,
};

const formatDateValue = (value) => {
    if (!value) return "";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("MM/DD/YYYY") : String(value);
};

const mapRowsFromApi = (rows = [], statusColumnDefinitions = []) => {
    const definitions = Array.isArray(statusColumnDefinitions) ? statusColumnDefinitions : [];
    const statusKeyToIndex = new Map(
        definitions.map((definition, index) => [
            normalizeStatusKey(definition.statusKey),
            index,
        ])
    );

    return (Array.isArray(rows) ? rows : []).map((item = {}, index) => {
        const mappedRow = {
            ...item,
            sn: item?.sn ?? item?.serialNumber ?? item?.sN ?? index + 1,
            client: item?.client ?? item?.clientName ?? "",
            clientId: item?.clientId ?? "",
            emailSentCount: item?.emailSentCount ?? item?.email_send_count ?? "",
            lastDayWhenEmailWasSent:
                item?.lastEmailSentRelative ??
                item?.lastDayWhenEmailWasSent ??
                item?.last_day_when_email_sent ??
                formatDateValue(item?.lastEmailSentIso ?? item?.lastEmailSentDate),
        };

        definitions.forEach((_, labelIndex) => {
            mappedRow[buildStatusAccessor(labelIndex)] = "";
        });

        (Array.isArray(item?.statusColumns) ? item.statusColumns : []).forEach(
            (statusColumn = {}) => {
                const labelIndex = statusKeyToIndex.get(
                    normalizeStatusKey(statusColumn?.statusKey)
                );
                if (labelIndex === undefined) return;
                mappedRow[buildStatusAccessor(labelIndex)] =
                    statusColumn?.count ?? statusColumn?.value ?? "";
            }
        );

        return mappedRow;
    });
};

const extractFilterResponse = (response = {}) => {
    const responseData = response?.data ?? response ?? {};
    const nestedData =
        responseData?.data && typeof responseData.data === "object"
            ? responseData.data
            : responseData;

    const rows = Array.isArray(nestedData?.rows)
        ? nestedData.rows
        : Array.isArray(responseData?.rows)
            ? responseData.rows
            : Array.isArray(responseData?.data)
                ? responseData.data
                : Array.isArray(responseData?.records)
                    ? responseData.records
                    : [];

    return { rows };
};

const LeadsAddedReport = () => {
    const [filterForm, setFilterForm] = useState({
        fromDate: "",
        toDate: "",
        jobCategoryIds: [],
        selectedClients: [],
        teamId: "",
        userId: "",
        [getUserAsyncFilterLabelFieldKey()]: "",
    });
    const [jobCategoryOptions, setJobCategoryOptions] = useState([]);
    const [teamOptions, setTeamOptions] = useState([]);
    const [reportData, setReportData] = useState([]);
    const [statusColumnDefinitions, setStatusColumnDefinitions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [statusModalContext, setStatusModalContext] = useState(null);
    const [filterErrors, setFilterErrors] = useState({});

    const filteredData = useMemo(() => reportData, [reportData]);
    const tableColumns = useMemo(
        () => buildTableColumns(statusColumnDefinitions),
        [statusColumnDefinitions]
    );

    const statusCandidatesFilterPayload = useMemo(
        () => ({
            jobCategoryIds: multiselectIdsOrNull(filterForm?.jobCategoryIds),
            dateFrom: normalizeMetadataFilterDateForApi(filterForm?.fromDate),
            dateTo: normalizeMetadataFilterDateForApi(filterForm?.toDate),
        }),
        [filterForm?.fromDate, filterForm?.toDate, filterForm?.jobCategoryIds]
    );

    useEffect(() => {
        const loadFilterOptions = async () => {
            const [jobCategories, teams] = await Promise.all([
                fetchLeadsAddedJobCategoryOptions(),
                fetchLeadsAddedTeamOptions(),
            ]);
            setJobCategoryOptions(Array.isArray(jobCategories) ? jobCategories : []);
            setTeamOptions(Array.isArray(teams) ? teams : []);
        };
        loadFilterOptions();
    }, []);

    const buildFilterPayload = () => ({
        dateFrom: statusCandidatesFilterPayload.dateFrom,
        dateTo: statusCandidatesFilterPayload.dateTo,
        jobCategoryIds: statusCandidatesFilterPayload.jobCategoryIds,
        clientIds: multiselectIdsOrNull(
            (filterForm?.selectedClients || []).map((option) => option?.value)
        ),
        teamId: strOrNull(filterForm?.teamId),
        userId: strOrNull(filterForm?.userId),
    });

    const getLeadsAddedReportList = async () => {
        try {
            await leadsAddedReportFilterSchema.validate(filterForm, { abortEarly: false });
            setFilterErrors({});
        } catch (validationError) {
            const nextErrors = {};
            validationError?.inner?.forEach((error) => {
                nextErrors[error.path] = error.message;
            });
            setFilterErrors(nextErrors);
            return;
        }

        setLoading(true);
        try {
            const response = await submitLeadsAddedReportFilterData(buildFilterPayload());
            const { rows } = extractFilterResponse(response);

            if (response == null || response?.success === false) {
                setReportData([]);
                setStatusColumnDefinitions([]);
                if (response?.message) {
                    toast.error(response.message);
                }
                return;
            }

            const nextStatusColumnDefinitions = extractStatusColumnDefinitions(rows);
            setStatusColumnDefinitions(nextStatusColumnDefinitions);
            setReportData(mapRowsFromApi(rows, nextStatusColumnDefinitions));
        } catch (error) {
            toast.error(error?.message || "Unable to load leads added report");
            setReportData([]);
            setStatusColumnDefinitions([]);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterFieldChange = (event) => {
        const { name, value } = event.target;
        setFilterForm((previous) => ({
            ...previous,
            [name]: value,
        }));
        if (filterErrors?.[name]) {
            setFilterErrors((previous) => ({
                ...previous,
                [name]: "",
            }));
        }
    };

    const handleJobCategoryMultiselectChange = (selectedOptions) => {
        const values = Array.isArray(selectedOptions)
            ? selectedOptions.map((option) => normalizeId(option?.value)).filter(Boolean)
            : [];
        setFilterForm((previous) => ({
            ...previous,
            jobCategoryIds: values,
        }));
    };

    const handleTeamChange = (option) => {
        setFilterForm((previous) => ({
            ...previous,
            teamId: option?.value || "",
        }));
    };

    const handleStatusCountClick = (row = {}, column = {}) => {
        const clientId = String(row?.clientId ?? "").trim();
        const statusKey = String(column?.statusKey ?? "").trim();
        if (!clientId || !statusKey) {
            toast.error("Unable to open candidate list for this count.");
            return;
        }

        setStatusModalContext({
            clientId,
            clientName: row?.client ?? "",
            statusKey,
            statusLabel: column?.Header ?? "",
        });
        setStatusModalOpen(true);
    };

    const closeStatusModal = () => {
        setStatusModalOpen(false);
        setStatusModalContext(null);
    };

    return (
        <React.Fragment>
            <div className="page-content leads-added-report-page">
                <Container fluid>
                    <Card>
                        <CardBody>
                            <h5 className="fw-bold mb-3">Leads Added Report</h5>
                            <hr />

                            <LegacyFilterToolbar className="w-100 leads-added-report-filter-toolbar">
                                <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-start">
                                    <Col
                                        xs="12"
                                        md="6"
                                        lg={2}
                                        className="metadata-filter-field metadata-filter-field--date"
                                    >
                                        <span className="manage-user-filter-label">Start Date</span>
                                        <div className="metadata-filter-field__control">
                                            <Input
                                                type="date"
                                                name="fromDate"
                                                className={`${LEGACY_FILTER_INPUT_CLASS}${
                                                    filterErrors?.fromDate ? " is-invalid" : ""
                                                }`}
                                                value={filterForm?.fromDate || ""}
                                                onChange={handleFilterFieldChange}
                                            />
                                        </div>
                                        <FilterFieldFeedback message={filterErrors?.fromDate} />
                                    </Col>
                                    <Col
                                        xs="12"
                                        md="6"
                                        lg={2}
                                        className="metadata-filter-field metadata-filter-field--date"
                                    >
                                        <span className="manage-user-filter-label">End Date</span>
                                        <div className="metadata-filter-field__control">
                                            <Input
                                                type="date"
                                                name="toDate"
                                                className={`${LEGACY_FILTER_INPUT_CLASS}${
                                                    filterErrors?.toDate ? " is-invalid" : ""
                                                }`}
                                                value={filterForm?.toDate || ""}
                                                onChange={handleFilterFieldChange}
                                            />
                                        </div>
                                        <FilterFieldFeedback message={filterErrors?.toDate} />
                                    </Col>
                                    <Col
                                        xs="12"
                                        md="6"
                                        lg={4}
                                        className="metadata-filter-field metadata-filter-field--multiselect leads-added-report-filter--wide"
                                    >
                                        <span className="manage-user-filter-label">Job Category</span>
                                        <div className="metadata-filter-field__control">
                                            <Select
                                                isMulti
                                                options={jobCategoryOptions}
                                                className="select2-selection"
                                                classNamePrefix="manage-user-filter-multiselect"
                                                placeholder="Job Category"
                                                closeMenuOnSelect={false}
                                                hideSelectedOptions={false}
                                                value={getSelectedMultiselectOptions(
                                                    jobCategoryOptions,
                                                    filterForm?.jobCategoryIds
                                                )}
                                                onChange={handleJobCategoryMultiselectChange}
                                                menuPortalTarget={
                                                    typeof document !== "undefined"
                                                        ? document.body
                                                        : null
                                                }
                                                menuPosition="fixed"
                                                styles={leadsAddedFilterMultiSelectStyles}
                                            />
                                        </div>
                                    </Col>
                                    <Col
                                        xs="12"
                                        md="6"
                                        lg={4}
                                        className="metadata-filter-field metadata-filter-field--multiselect leads-added-report-filter--wide"
                                    >
                                        <span className="manage-user-filter-label">Client</span>
                                        <div className="metadata-filter-field__control">
                                            <AsyncClientMultiSelect
                                                key={`leads-added-clients-${(filterForm?.selectedClients || [])
                                                    .map((option) => option?.value)
                                                    .join("-")}`}
                                                pageName="ReportAllStatuses"
                                                value={filterForm?.selectedClients || []}
                                                onChange={(selectedOptions) =>
                                                    setFilterForm((previous) => ({
                                                        ...previous,
                                                        selectedClients: Array.isArray(
                                                            selectedOptions
                                                        )
                                                            ? selectedOptions
                                                            : [],
                                                    }))
                                                }
                                                placeholder="Select Client"
                                                styles={leadsAddedFilterMultiSelectStyles}
                                            />
                                        </div>
                                    </Col>
                                </Row>
                                <Row
                                    className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end mt-2 mt-lg-0"
                                >
                                    <Col
                                        xs="12"
                                        md="4"
                                        lg={2}
                                        className="metadata-filter-field metadata-filter-field--dropdown"
                                    >
                                        <span className="manage-user-filter-label">Team</span>
                                        <div className="metadata-filter-field__control">
                                            <StaticFilterSelect
                                                options={teamOptions}
                                                placeholder="Select Team"
                                                value={filterForm?.teamId || ""}
                                                onChange={handleTeamChange}
                                            />
                                        </div>
                                    </Col>
                                    <Col
                                        xs="12"
                                        md="4"
                                        lg={2}
                                        className="metadata-filter-field metadata-filter-field--dropdown"
                                    >
                                        <span className="manage-user-filter-label">User</span>
                                        <div className="metadata-filter-field__control">
                                            <AsyncUserSelect
                                                key={`leads-added-user-${filterForm?.userId || ""}`}
                                                pageName="LeadsAddedReport"
                                                value={filterForm?.userId || ""}
                                                initialOption={buildUserAsyncFilterInitialOption(
                                                    filterForm?.userId,
                                                    filterForm[getUserAsyncFilterLabelFieldKey()]
                                                )}
                                                onChange={(option) =>
                                                    setFilterForm((previous) =>
                                                        applyUserAsyncFilterOptionToFormState(
                                                            previous,
                                                            option
                                                        )
                                                    )
                                                }
                                                placeholder="Select User"
                                            />
                                        </div>
                                    </Col>
                                    <Col
                                        xs="12"
                                        md="4"
                                        lg={2}
                                        className="metadata-filter-field metadata-filter-field--actions ms-lg-auto"
                                    >
                                        <span
                                            className="manage-user-filter-label"
                                            aria-hidden="true"
                                        >
                                            &nbsp;
                                        </span>
                                        <div className="metadata-filter-field__control">
                                            <Button
                                                color="primary"
                                                className="w-100 manage-user-search-btn"
                                                type="button"
                                                onClick={getLeadsAddedReportList}
                                            >
                                                <i
                                                    className="fa fa-search me-1"
                                                    aria-hidden="true"
                                                />
                                                Search
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </LegacyFilterToolbar>

                            {loading ? (
                                <Loaders />
                            ) : (
                                <LeadsAddedReportTable
                                    columns={tableColumns}
                                    data={filteredData}
                                    onStatusCountClick={handleStatusCountClick}
                                    pageName="LeadsAddedReport"
                                />
                            )}
                        </CardBody>
                    </Card>

                    <LeadsAddedStatusCandidatesModal
                        isOpen={statusModalOpen}
                        onClose={closeStatusModal}
                        clientId={statusModalContext?.clientId}
                        clientName={statusModalContext?.clientName}
                        statusKey={statusModalContext?.statusKey}
                        statusLabel={statusModalContext?.statusLabel}
                        filterPayload={statusCandidatesFilterPayload}
                    />
                </Container>
            </div>
        </React.Fragment>
    );
};

LeadsAddedReport.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(LeadsAddedReport);
